import { numeric } from '@vuelidate/validators';
import { unicodeRegexpValidationRules, validationRulesLib } from 'shared';
import { isValidIIN } from '@aviata/iin';

import { documentValidator } from './document-validator';
import {
  rwPassengersData as passengersData,
  rwPassengersDataTypes as passengersTypes,
} from '../../../entities/rw-passengers-data';
import type {
  ValidationSchema,
  InfoForBirthDayValidation,
  InfoForBaseValidation,
  LocalizedValidators,
} from '../types';
import {
  CYRILLIC_ONLY_DOC_TYPES,
  LATIN_ONLY_DOC_TYPES,
  RU_EN_ONLY_DOC_TYPES,
} from '../config';

export const getAlphabetValidation = (
  passengerData: passengersTypes.PassengerData,
  localizedValidators: LocalizedValidators,
  messagePathSuffix: string,
) => {
  if (RU_EN_ONLY_DOC_TYPES.includes(passengerData.documentType)) {
    return {
      ruEnOnly: localizedValidators.withI18nMessage(
        validationRulesLib.withMessagePath(
          unicodeRegexpValidationRules.isValidRuEnInitials,
          messagePathSuffix,
        ),
      ),
    };
  }

  if (LATIN_ONLY_DOC_TYPES.includes(passengerData.documentType)) {
    return {
      latinOnly: localizedValidators.withI18nMessage(
        validationRulesLib.withMessagePath(
          unicodeRegexpValidationRules.isValidLatinInitials,
          messagePathSuffix,
        ),
      ),
    };
  }

  if (CYRILLIC_ONLY_DOC_TYPES.includes(passengerData.documentType)) {
    return {
      cyrillicOnly: localizedValidators.withI18nMessage(
        validationRulesLib.withMessagePath(
          unicodeRegexpValidationRules.isValidCyrillicInitials,
          messagePathSuffix,
        ),
      ),
    };
  }
};

export const addBirthdayValidation = (
  info: InfoForBirthDayValidation,
  localizedValidators: LocalizedValidators,
) => {
  /**
   * 1. If used in railways search app, then should compare to departure date.
   * 2. If used in passengers app, compare to current time.
   * */
  const dateToGetAge = info.departureDate ?? Date();

  let ageBetween: validationRulesLib.AgeConstraints = {
    dateToGetAge,
    min: 0,
    max: 120,
  };
  let passengerType = passengersTypes.FareType.ADULT;

  if (info.isChild) {
    ageBetween = {
      ...ageBetween,
      max: info.isFemaleCar && !info.isFemale ? 7 : 15,
    };
    passengerType = passengersTypes.FareType.CHILD;
  }

  const adultMaxAge = validationRulesLib.initMaxAgeValidator(
    ageBetween,
    passengerType,
  );
  const adultMinAge = validationRulesLib.initMinAgeValidator(
    ageBetween,
    passengerType,
  );

  return {
    ...info.validation,
    dateOfBirth: {
      requiredBirthDate: localizedValidators.required,
      isValidDate: localizedValidators.withI18nMessage(
        validationRulesLib.isValidDate,
      ),
      maxAge: localizedValidators.withI18nMessage(adultMaxAge),
      minAge: localizedValidators.withI18nMessage(adultMinAge),
    },
  };
};

/** Base validation consists of fields which are always present in railways search app's passenger form */
export const getBaseValidation = (
  passenger: InfoForBaseValidation,
  localizedValidators: LocalizedValidators,
) => {
  const { required, withI18nMessage } = localizedValidators;

  const isIinRequired =
    passenger.value.iin.hasIin ||
    passengersData.lib.isIinRequired(passenger.value.documentType);

  const validation = {
    firstName: {
      requiredFirstName: required,
      ...getAlphabetValidation(
        passenger.value,
        localizedValidators,
        'firstName',
      ),
    },
    lastName: {
      requiredLastName: required,
      ...getAlphabetValidation(
        passenger.value,
        localizedValidators,
        'lastName',
      ),
    },
    documentNumber: {
      requiredDocNumber: required,
      documentNumber: withI18nMessage((value: string) =>
        documentValidator(value, passenger.value.documentType),
      ),
    },
  } as ValidationSchema;

  if (isIinRequired) {
    validation.iin = {
      value: {
        requiredIin: required,
        numericIin: withI18nMessage(numeric),
        iinLength: withI18nMessage((value: string) => value.length === 12),
        isValidIin: withI18nMessage(isValidIIN),
      },
    };
  }

  return validation;
};
